import clsx from 'clsx';
import { motion } from 'framer-motion';
import { ComponentPropsWithoutRef, Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { useClickAway, useHover } from 'react-use';
import { fadeInOut } from '../../animation';
import { useRootBodyScrollLock } from '../../hooks';
import { PopperContainerChildrenArgs } from './components/PopperContainer';

export interface PopperProps extends Pick<ComponentPropsWithoutRef<'div'>, 'className'>, PopperContainerChildrenArgs {
  shouldHide?: boolean;
  ignoreHideTrigger?: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  floatingElement?:
    | ((c: {
        setOpen: (open: boolean) => void;
        setPopperInvisible: Dispatch<SetStateAction<boolean>>;
        forceFloatingElementUpdate?: (() => void) | null;
      }) => JSX.Element)
    | JSX.Element;
}

const Popper: FC<PopperProps> = ({
  setOpen,
  className,
  shouldHide,
  setPopperEl,
  popperStyles,
  floatingElement,
  popperAttributes,
  ignoreHideTrigger,
  forceFloatingElementUpdate,
}) => {
  const [locked, setLocked] = useState(false);
  const popperRef = useRef<HTMLDivElement>(null);
  const [popperInvisible, setPopperInvisible] = useState(false);
  const [enterAnimationComplete, setEnterAnimationComplete] = useState(false);

  // Wait for enter animation to complete before enabling click-away-to-hide behavior
  useClickAway(popperRef, () => {
    if (!ignoreHideTrigger && enterAnimationComplete) setOpen(false);
  });

  useRootBodyScrollLock(locked);

  // Reset enterAnimationComplete when the component is unmounted
  useEffect(() => setEnterAnimationComplete(false), []);

  const [hoverableFloatingElement, hovered] = useHover(
    <motion.div
      {...fadeInOut}
      ref={popperRef}
      style={popperStyles.popper}
      id="popperCustomComponent"
      {...popperAttributes.popper}
      onAnimationComplete={() => setEnterAnimationComplete(true)}
      className={clsx(
        'z-[999]',
        className,
        // Since the potentially clickable popper content can animate into view from the direction of the trigger,
        // we need to prevent pointer events until the animation is complete.
        !enterAnimationComplete && '!pointer-events-none',
        popperInvisible && '!pointer-events-none !invisible'
      )}
    >
      {typeof floatingElement === 'function'
        ? floatingElement({
            setOpen,
            setPopperInvisible,
            forceFloatingElementUpdate,
          })
        : floatingElement}
    </motion.div>
  );

  useEffect(() => {
    if (popperRef.current) {
      setPopperEl(popperRef.current);
    }
  }, [popperRef.current]);

  useEffect(() => {
    if (hovered) {
      setLocked(true);
    } else {
      setLocked(false);
    }
  }, [hovered]);

  useEffect(() => {
    if (!hovered && shouldHide) {
      setOpen(false);
    }
  }, [shouldHide]);

  return hoverableFloatingElement;
};

export default Popper;
